import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import Nav from 'gatsby-theme-menus/src/components/nav/index';
import { useNav } from 'gatsby-theme-menus/src/components/nav/provider';
import useMenuItems from 'gatsby-theme-menus/src/hooks/use-menu-items';
import ListNav from 'gatsby-theme-menus/src/components/nav/list-nav';

import PagebuilderProviders from 'gatsby-theme-pagebuilder/src/components/providers';
import PagebuilderButton from 'gatsby-theme-pagebuilder/src/components/button/index';

import {
  minBreakpoint,
  maxBreakpoint,
} from 'gatsby-theme-core/src/helpers/media';
import Container from 'gatsby-theme-core/src/components/container';
import Image from 'gatsby-theme-core/src/components/image';
import useHeader from '../../hooks/use-header';

import NavBeam from './nav-beam';
import Logo from './logo';
import Item from './item';
import Hamburger from './hamburger';

const Header = styled.header`
  ${({ $stickyUntil, theme }) => $stickyUntil && css`
    position: sticky;
    top: 0;
    z-index: 4; /* push over Swiper slides and price Floater */
    @media all and (min-width: ${theme.breakingpoints[$stickyUntil]}) {
      position: static;
    }
  `}
`;

const CoreHeader = ({
  className,
}) => {
  const { breakingpoints } = useTheme();
  const {
    stickyUntil,
    logo,
    nav,
    items,
  } = useHeader();
  const {
    justify,
    beam,
    hamburgerUntil,
  } = nav;

  const { open, setOpen } = useNav();
  const { menus } = useMenuItems();
  const { title, position } = menus[0];
  const menuName = `${title} (${position})`;

  return (
    <Header $stickyUntil={stickyUntil} className={className}>
      <Container>
        <Inner $beam={beam} $logoJustify={logo.justify} $navJustify={justify}>
          <Logo image={logo.logo} />
          {!beam && (
            <Nav navBreakpoint={breakingpoints[hamburgerUntil]} />
          )}
          <PagebuilderProviders>
            <Items>
              {items.map(({
                id,
                image,
                button,
                visibleFrom,
                visibleUntil,
                iconOnlyUntil,
              }) => (
                <Item
                  key={id}
                  $iconOnlyUntil={iconOnlyUntil}
                  css={`
                    ${minBreakpoint(visibleFrom)}
                    ${maxBreakpoint(visibleUntil)}
                  `}
                >
                  {image && (
                    <Image image={image.image} />
                  )}
                  {button && (
                    <PagebuilderButton
                      {...button}
                    />
                  )}
                </Item>
              ))}
              <Item className="header-hamburger-item" css={maxBreakpoint(hamburgerUntil)}>
                <Hamburger
                  menuName={menuName}
                  onClick={() => setOpen(!open)}
                />
              </Item>
            </Items>
          </PagebuilderProviders>
        </Inner>
      </Container>

      {beam && (
        <NavBeam $navJustify={justify}>
          <Container>
            <Nav navBreakpoint={breakingpoints[hamburgerUntil]} />
          </Container>
        </NavBeam>
      )}
    </Header>
  );
};

const Items = styled.div`
  display: flex;
`;

export const Inner = styled.div`
  padding: 1.5rem 0;
  ${({ $beam }) => !$beam && css`
    padding: 1rem 0;
  `}
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;


  > *:not(:first-child) { // not the logo
    flex-shrink: 0; // make sure buttons dont start making new lines
  }

  *[aria-label="Terug naar de homepage"] {
    ${({ $logoJustify }) => $logoJustify === 'right' && css`
      margin-left: auto;
    `};
    ${({ $logoJustify }) => $logoJustify === 'center' && css`
      flex-grow: 1;
    `};
  }

  // the not navbar version:
  ${ListNav} {
    flex-shrink: 1;
    flex-grow: 1;
    display: flex;
    justify-content: ${({ $navJustify }) => $navJustify || 'center'};
  }
`;

export default styled(CoreHeader)`
  background-color: white;
`;
